import { useState, FormEvent } from 'react';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    inquiry_type: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });
      const response = await fetch('https://readdy.ai/api/form/d5dkammfmrgp9cvlvj8g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', inquiry_type: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-16 md:py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* セクションヘッダー */}
        <div className="flex items-start gap-4 md:gap-6 mb-10 md:mb-16">
          <div className="flex-shrink-0 pt-1">
            <div className="w-px h-12 md:h-16 bg-[#1a3a5c]"></div>
          </div>
          <div>
            <p className="text-[#1a3a5c] text-xs tracking-[0.3em] font-medium uppercase mb-2">Contact</p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              お問合せ
            </h2>
            <p className="text-slate-500 text-sm">お気軽にご相談ください。通常1営業日以内にご返信いたします。</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 md:gap-12">

          {/* 左：電話・案内 */}
          <div className="lg:col-span-2 space-y-5 md:space-y-6">
            {/* 電話 */}
            <div className="bg-[#1a3a5c] p-6 md:p-8">
              <p className="text-blue-300 text-xs tracking-[0.3em] uppercase mb-4">Phone</p>
              <p className="text-white/70 text-sm mb-3">お電話でのお問合せ</p>
              <a href="tel:06-7777-3813" className="block text-white text-2xl md:text-3xl font-bold tracking-wider hover:text-blue-200 transition-colors cursor-pointer mb-1"
                style={{ fontFamily: "'Noto Serif JP', serif" }}>
                06-7777-3813
              </a>
              <p className="text-white/50 text-xs mb-3">代表電話</p>
              <a href="tel:070-9096-1669" className="block text-white text-2xl md:text-3xl font-bold tracking-wider hover:text-blue-200 transition-colors cursor-pointer mb-1"
                style={{ fontFamily: "'Noto Serif JP', serif" }}>
                070-9096-1669
              </a>
              <p className="text-white/50 text-xs mb-3">代表直通</p>
              <div className="border-t border-white/10 pt-4">
                <p className="text-white/60 text-xs leading-relaxed">
                  年中無休（不定休）<br />
                  午前9時 〜 深夜24時まで対応
                </p>
              </div>
            </div>

            {/* 事務所写真 */}
            <div className="w-full h-[180px] md:h-[220px] overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/8383302d690895ced525001174a8f68f.jpg"
                alt="事務所"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* 案内事項 */}
            <div className="border border-slate-200 bg-white p-5 md:p-6 space-y-3 md:space-y-4">
              {[
                { icon: 'ri-time-line', text: '返信は通常1営業日以内' },
                { icon: 'ri-shield-check-line', text: '個人情報は厳重に管理いたします' },
                { icon: 'ri-chat-3-line', text: '初回相談は無料です' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-7 h-7 flex items-center justify-center bg-slate-100 flex-shrink-0">
                    <i className={`${item.icon} text-[#1a3a5c] text-sm`}></i>
                  </div>
                  <p className="text-slate-600 text-sm">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 右：フォーム */}
          <div className="lg:col-span-3 bg-white border border-slate-200 p-6 md:p-10">
            <p className="text-slate-400 text-xs tracking-[0.3em] uppercase mb-5 md:mb-6">Email Form</p>
            <form onSubmit={handleSubmit} data-readdy-form className="space-y-4 md:space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                <div>
                  <label htmlFor="name" className="block text-xs font-bold text-slate-600 tracking-wider mb-2">
                    お名前 <span className="text-[#1a3a5c]">*</span>
                  </label>
                  <input
                    type="text" id="name" name="name" value={formData.name}
                    onChange={handleChange} required
                    className="w-full px-4 py-3 border border-slate-300 focus:border-[#1a3a5c] focus:outline-none transition-colors text-sm bg-slate-50 focus:bg-white"
                    placeholder="山田太郎"
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-xs font-bold text-slate-600 tracking-wider mb-2">
                    会社名・団体名
                  </label>
                  <input
                    type="text" id="company" name="company" value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 focus:border-[#1a3a5c] focus:outline-none transition-colors text-sm bg-slate-50 focus:bg-white"
                    placeholder="株式会社〇〇"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                <div>
                  <label htmlFor="email" className="block text-xs font-bold text-slate-600 tracking-wider mb-2">
                    メールアドレス <span className="text-[#1a3a5c]">*</span>
                  </label>
                  <input
                    type="email" id="email" name="email" value={formData.email}
                    onChange={handleChange} required
                    className="w-full px-4 py-3 border border-slate-300 focus:border-[#1a3a5c] focus:outline-none transition-colors text-sm bg-slate-50 focus:bg-white"
                    placeholder="example@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-xs font-bold text-slate-600 tracking-wider mb-2">
                    電話番号
                  </label>
                  <input
                    type="tel" id="phone" name="phone" value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 focus:border-[#1a3a5c] focus:outline-none transition-colors text-sm bg-slate-50 focus:bg-white"
                    placeholder="06-1234-5678"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="inquiry_type" className="block text-xs font-bold text-slate-600 tracking-wider mb-2">
                  お問合せ種別 <span className="text-[#1a3a5c]">*</span>
                </label>
                <select
                  id="inquiry_type" name="inquiry_type" value={formData.inquiry_type}
                  onChange={handleChange} required
                  className="w-full px-4 py-3 border border-slate-300 focus:border-[#1a3a5c] focus:outline-none transition-colors text-sm bg-slate-50 focus:bg-white cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="受け入れ企業">受け入れ企業からのお問合せ</option>
                  <option value="登録支援機関">登録支援機関からのお問合せ</option>
                  <option value="人材紹介会社">人材紹介会社からのお問合せ</option>
                  <option value="その他">その他</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-xs font-bold text-slate-600 tracking-wider mb-2">
                  お問合せ内容 <span className="text-[#1a3a5c]">*</span>
                </label>
                <textarea
                  id="message" name="message" value={formData.message}
                  onChange={handleChange} required maxLength={500} rows={5}
                  className="w-full px-4 py-3 border border-slate-300 focus:border-[#1a3a5c] focus:outline-none transition-colors resize-none text-sm bg-slate-50 focus:bg-white"
                  placeholder="お問合せ内容をご記入ください（500文字以内）"
                />
                <p className="text-xs text-slate-400 mt-1 text-right">{formData.message.length}/500文字</p>
              </div>

              {submitStatus === 'success' && (
                <div className="border border-[#1a3a5c]/30 bg-blue-50 p-4 md:p-5 flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-2xl text-[#1a3a5c] flex-shrink-0"></i>
                  <div>
                    <p className="font-bold text-slate-800 text-sm">お問合せを受け付けました。</p>
                    <p className="text-slate-500 text-xs mt-0.5">担当者より折り返しご連絡いたします。</p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="border border-red-200 bg-red-50 p-4 md:p-5 flex items-start gap-3">
                  <i className="ri-error-warning-line text-2xl text-red-500 flex-shrink-0"></i>
                  <div>
                    <p className="font-bold text-red-700 text-sm">送信に失敗しました。</p>
                    <p className="text-red-500 text-xs mt-0.5">お手数ですが、お電話にてお問合せください。</p>
                  </div>
                </div>
              )}

              <button
                type="submit" disabled={isSubmitting}
                className="w-full bg-[#1a3a5c] hover:bg-[#0f2d52] text-white font-bold py-4 px-8 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm tracking-widest whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>送信中...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-send-plane-line"></i>送信する
                  </span>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* フッター */}
        <div className="mt-12 md:mt-16 pt-6 md:pt-8 border-t border-slate-200 flex flex-col md:flex-row items-center justify-between gap-3 md:gap-4">
          <p className="text-slate-400 text-xs tracking-wider text-center md:text-left">
            © 2024 行政書士ＭＫ法務事務所　All Rights Reserved.
          </p>
          <p className="text-slate-400 text-xs text-center md:text-right">
            〒561-0802 大阪府豊中市曽根西町1-9-28 ＲＳビルディング4F
          </p>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
